import { createFileRoute } from '@tanstack/react-router';

import { type ProgressData } from 'api/types/ProgressData';

import { defer } from 'helpers/defer';

import { getAppProgressOptions } from 'hooks/data/useAppProgress';
import { getChapterInfoOptions } from 'hooks/data/useChapterInfo';
import { getSoureAppsOptions } from 'hooks/data/useSourceAppData';

import { RetainOverviewView } from 'views/RetainOverviewView/RetainOverviewView';

export const Route = createFileRoute('/_auth/_buttonView/_layout/retain/_app/$id/')({
  component: RetainOverviewView,
  staticData: {
    back: {
      to: '/retain/',
    },
  },
  loader: async ({ context: { queryClient }, params }) => {
    const apps = await queryClient.ensureQueryData(getSoureAppsOptions());

    const app = apps.find(({ id }) => {
      return id === params.id;
    });

    const microAppIds = (app?.microLearnings || []).flatMap((name) => {
      return apps
        .filter((app) => {
          return app.categories.some((cat) => {
            return cat.name === name;
          });
        })
        .map(({ id }) => {
          return id;
        });
    });

    type Result = { progress: ProgressData; micro: ProgressData[] };

    const allProgress: Promise<Result> = new Promise((resolve, reject) => {
      queryClient
        .ensureQueryData(getAppProgressOptions({ resource: [params.id, ...microAppIds] }))
        .then((data) => {
          // Since API returns an object when only a single app is requested, we transform it into an array so that we don't have to worry about data type.
          let progress: ProgressData = {} as ProgressData;
          const micro: ProgressData[] = [];

          [data].flat().forEach((item) => {
            if (microAppIds.includes(item.id)) {
              micro.push(item);
            } else {
              progress = item;
            }
          });

          if (progress === undefined) {
            reject(new Error('Error fetching progress data'));
          }

          resolve({ progress, micro });
        })
        .catch((e) => {
          console.error(e);
        });
    });

    const chapters = await queryClient.ensureQueryData(getChapterInfoOptions(params));

    return {
      app,
      apps,
      chapters,
      defer: {
        allProgress: defer(allProgress),
      },
    };
  },
});
